import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  Select,
  Switch,
  Space,
  Typography,
  message,
  Table,
} from "antd";
import {
  InboxOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import FormModal from "../../components/FormModal";
import DialogFooter from "../../components/DialogFooter";
import { fetchTableDetails } from "./HiveAPI";
import { extractMsg } from "../../utils/errorUtils";

const { Dragger } = Upload;
const { Option } = Select;

export function CreateTableDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const formatValue = Form.useWatch("format", form);
  return (
    <FormModal
      title="Create Hive Table"
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      onFinish={async (v) => {
        try {
          const payload = {
            tableName: v.tableName,
            columns: v.columns,
            hdfsLocation: v.hdfsLocation,
            format: v.format,
            hasHeader: v.format === "DELIMITED" ? v.hasHeader || false : false,
          };
          if (v.format === "DELIMITED") payload.delimiter = v.delimiter;
          await onSave(payload);
          form.resetFields();
          onClose();
        } catch (error) {
          message.error(extractMsg(error, "Failed to create table"));
          console.error("Failed to create table", error);
        }
      }}
      form={form}
      submitText="Save"
      width={800}
      initialValues={{
        columns: [{ colName: "", dataType: "STRING" }],
        hasHeader: false,
      }}
    >
      <Form.Item
        name="tableName"
        label="Table Name *"
        rules={[{ required: true }]}
      >
        <Input placeholder="Enter table name" />
      </Form.Item>
      <Form.Item
        name="hdfsLocation"
        label="HDFS Path *"
        rules={[{ required: true }]}
      >
        <Input placeholder="Enter HDFS path" />
      </Form.Item>
      <Form.Item
        name="format"
        label="File Format *"
        rules={[{ required: true }]}
      >
        <Select placeholder="Select format">
          <Option value="DELIMITED">DELIMITED</Option>
          <Option value="PARQUET">PARQUET</Option>
          <Option value="ORC">ORC</Option>
        </Select>
      </Form.Item>
      {formatValue === "DELIMITED" && (
        <Space size="large" style={{ display: "flex", marginBottom: 24 }}>
          <Form.Item
            name="delimiter"
            label="Delimiter"
            rules={[{ required: true }]}
            style={{ width: 250, marginBottom: 0 }}
          >
            <Select placeholder="Select delimiter">
              <Option value="comma">Comma</Option>
              <Option value="tab">Tab</Option>
              <Option value="semicolon">Semicolon</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="hasHeader"
            label="Has Header"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <Switch />
          </Form.Item>
        </Space>
      )}
      <Typography.Text strong>Columns *</Typography.Text>
      <Form.List
        name="columns"
        rules={[
          {
            validator: async (_, cols) => {
              if (!cols || cols.length < 1)
                return Promise.reject(
                  new Error("At least one column is required"),
                );
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <div style={{ marginTop: 8 }}>
            {fields.map(({ key, name, ...rest }) => (
              <Space
                key={key}
                style={{ display: "flex", marginBottom: 8 }}
                align="baseline"
              >
                <Form.Item
                  {...rest}
                  name={[name, "colName"]}
                  rules={[
                    { required: true, message: "Missing column name" },
                    {
                      pattern: /^[A-Za-z_0-9]+$/,
                      message: "Alphanumeric and underscores only",
                    },
                  ]}
                >
                  <Input placeholder="Column Name" />
                </Form.Item>
                <Form.Item
                  {...rest}
                  name={[name, "dataType"]}
                  rules={[{ required: true, message: "Missing type" }]}
                  style={{ width: 150 }}
                >
                  <Select placeholder="Select Type">
                    <Option value="STRING">STRING</Option>
                    <Option value="INT">INT</Option>
                    <Option value="DOUBLE">DOUBLE</Option>
                    <Option value="DATE">DATE</Option>
                    <Option value="TIMESTAMP">TIMESTAMP</Option>
                    <Option value="BOOLEAN">BOOLEAN</Option>
                    <Option value="BIGINT">BIGINT</Option>
                    <Option value="FLOAT">FLOAT</Option>
                    <Option value="SMALLINT">SMALLINT</Option>
                    <Option value="TINYINT">TINYINT</Option>
                    <Option value="DECIMAL">DECIMAL</Option>
                    <Option value="CHAR">CHAR</Option>
                    <Option value="BINARY">BINARY</Option>
                  </Select>
                </Form.Item>
                {fields.length > 1 && (
                  <MinusCircleOutlined
                    onClick={() => remove(name)}
                    style={{ color: "red" }}
                  />
                )}
              </Space>
            ))}
            <Form.Item>
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Add Column
              </Button>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </div>
        )}
      </Form.List>
    </FormModal>
  );
}

export function UploadJsonDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const handleFinish = async () => {
    if (!fileList.length) {
      message.error("Please select a JSON file to upload");
      return;
    }
    setLoading(true);
    try {
      await onSave(fileList[0]);
      form.resetFields();
      setFileList([]);
      onClose();
    } catch (error) {
      console.error("Failed to upload JSON file", error);
      message.error(extractMsg(error, "Failed to upload JSON file"));
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    onRemove: () => setFileList([]),
    beforeUpload: (file) => {
      if (!file.name.toLowerCase().endsWith(".json")) {
        message.error("Please select a valid JSON file");
        return Upload.LIST_IGNORE;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error("File size exceeds the 10 MB limit");
        return Upload.LIST_IGNORE;
      }
      setFileList([file]);
      return false;
    },
    fileList,
    maxCount: 1,
    accept: ".json",
  };

  return (
    <Modal
      title="Upload JSON File"
      open={open}
      onCancel={() => {
        form.resetFields();
        setFileList([]);
        onClose();
      }}
      footer={null}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item label="Upload Database Schema (JSON)">
          <Dragger {...uploadProps}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag `.json` file to this area to upload (max 10 MB).
            </p>
          </Dragger>
        </Form.Item>
        <DialogFooter
          onCancel={() => {
            form.resetFields();
            setFileList([]);
            onClose();
          }}
          onSave={form.submit}
          loading={loading}
          saveText="Upload"
        />
      </Form>
    </Modal>
  );
}

export function DatabaseDetailsDialog({ open, onClose, tableName }) {
  const [loading, setLoading] = useState(false);
  const [structure, setStructure] = useState([]);
  useEffect(() => {
    if (open && tableName) {
      setLoading(true);
      fetchTableDetails(tableName)
        .then((res) => {
          if (res?.columns) setStructure(res.columns);
        })
        .catch((e) => {
          console.error(e);
          message.error(extractMsg(e, "Failed to fetch table details"));
        })
        .finally(() => setLoading(false));
    }
  }, [open, tableName, fetchTableDetails]);
  const structCols = [
    { title: "Column Name", dataIndex: "columnName", key: "columnName" },
    { title: "Data Type", dataIndex: "dataType", key: "dataType" },
  ];
  return (
    <Modal
      title={`Table Metadata: ${tableName}`}
      open={open}
      onCancel={onClose}
      footer={
        <DialogFooter onCancel={onClose} onSave={onClose} saveText="Close" />
      }
      width={1000}
    >
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={5}>Schema Structure</Typography.Title>
        <Table
          columns={structCols}
          dataSource={structure}
          rowKey="columnName"
          pagination={false}
          size="small"
          loading={loading}
        />
      </div>
    </Modal>
  );
}
